import { encrypt, useMount } from '@apia/util';
import {
  classToValidate,
  classToValidationFunction,
  Input,
} from '@apia/validations';
import React from 'react';
import { TApiaApiField } from '../ApiaApiHandler';

declare global {
  interface Window {
    SALT: string;
    IV: string;
    PASSPHRASE: string;
    KEY_SIZE: number;
    ITERATION_COUNT: number;
  }
}

/**
 * Permite cargar en forma asíncrona un modal que se encuentre dentro del
 * directorio /api/modals
 * */
export const getModal = (path: string) => {
  return React.lazy<React.ComponentType<TApiaApiField>>(() => {
    return new Promise<{
      default: React.ComponentType<TApiaApiField>;
    }>((resolve) => {
      import(
        /* webpackChunkName: "handlerModal" */
        /* webpackInclude: /\.tsx?$/ */
        `/api/modals/${path}`
      )
        .then((result: unknown) => {
          resolve(
            result as {
              default: React.ComponentType<TApiaApiField>;
            },
          );
        })
        .catch((error: unknown) => {
          resolve({
            default: () => {
              console.error(error);
              throw new Error(
                `The above error ocurred at component ApiaApiHandler/${path}, does it exist?`,
              );
            },
          });
        });
    });
  });
};

export const ApiaApiInput = (props: TApiaApiField) => {
  const element = React.useMemo(() => props.element, [props.element]);

  const [Submodal, setSubmodal] = React.useState<React.LazyExoticComponent<
    React.ComponentType<TApiaApiField>
  > | null>(null);

  useMount(() => {
    if (element.modalFunction) {
      const found = element.modalFunction.match(/(?:fnc)?(\w+)\(?\)?$/);
      if (found) {
        const modalName = found[found.length - 1];
        setSubmodal(getModal(modalName));
      }
    }
  });

  const className = React.useMemo(
    () =>
      element.class
        ? `handler__${element.type} ${element.class}`
        : `handler__${element.type}`,
    [element.class, element.type],
  );
  const validationRules = React.useMemo(
    () => ({
      required: element.mandatory,
      maxLength: element.maxlength ? Number(element.maxlength) : undefined,
      pattern: element.regExp,
      patternMessage: element.regExpMessage,
      ...classToValidate(element.class),
    }),
    [
      element.class,
      element.mandatory,
      element.maxlength,
      element.regExp,
      element.regExpMessage,
    ],
  );
  const validationFunction = React.useMemo(
    () => classToValidationFunction(element.class),
    [element.class],
  );
  const submitValueParser = React.useCallback(
    (value: string) => {
      if (element.type === 'password') {
        return encrypt(
          window.SALT,
          window.IV,
          window.PASSPHRASE,
          value,
          Number(window.KEY_SIZE),
          Number(window.ITERATION_COUNT),
        );
      }
      return value;
    },
    [element.type],
  );

  if (element.modalFunction) {
    if (Submodal) {
      return (
        <React.Suspense>
          <Submodal {...props} element={element} />
        </React.Suspense>
      );
    }
    return null;
  }

  return (
    <Input
      type={element.isADate ? 'date' : element.type}
      className={className}
      name={element.id || element.name}
      label={element.text}
      title={element.title || element.text}
      value={element.value}
      readOnly={element.readonly}
      disabled={element.disabled}
      validationFunction={validationFunction}
      validationRules={validationRules}
      submitValueParser={submitValueParser}
      onChange={element.onChange}
      autoComplete={element.type === 'password' ? 'new-password' : undefined}
    />
  );
};

ApiaApiInput.displayName = 'ApiaApiInput';
